import * as echarts from 'echarts';
import ReactECharts from 'echarts-for-react';
import React from 'react';
import { useColorStore } from '@/store/colorStore';

type ColorKey =
  | 'accentBlue'
  | 'accentYellow'
  | 'accentGrape'
  | 'accentCyan'
  | 'accentOrange'
  | 'accentSoftGreen'
  | 'accentSoftRed'
  | 'accentSoftYellow'
  | 'accentSoftGrape'
  | 'accentDeepBlue'
  | 'danger'
  | 'warning'
  | 'greenPrimary'
  | 'yellowPrimary'
  | 'greyPrimary';

export interface SeriesItem {
  name: string;
  data: number[];
  color: ColorKey;
  areaStyle?: {
    startColor: ColorKey;
    endColor: ColorKey;
    opacity?: number;
  };
}

interface LineChartProps {
  title?: string;
  xAxisData: string[];
  series: SeriesItem[];
  bottom?: string;
  boundaryGap?: boolean;
}

const LineChart = ({
  title,
  xAxisData,
  series,
  bottom = '10%',
  boundaryGap = true,
}: LineChartProps) => {
  const { NEWCOLOR, chart } = useColorStore();

  const option = {
    title: {
      text: title,
      left: 'left',
      top: 0,
      textStyle: {
        color: chart.legendText,
        fontSize: 14,
        fontWeight: 600,
      },
    },
    tooltip: {
      trigger: 'axis',
    },
    legend: {
      bottom: 0,
      icon: 'circle',
      textStyle: {
        color: chart.legendText,
      },
      formatter: (name: string) => name.toUpperCase(),
    },
    grid: {
      left: boundaryGap ? '0%' : '4%',
      right: boundaryGap ? '0%' : '4%',
      top: '12%',
      bottom: `${bottom}`,
      containLabel: true,
    },

    xAxis: {
      type: 'category',
      data: xAxisData,
      boundaryGap,
      axisLine: {
        lineStyle: {
          color: chart.axisLine,
        },
      },
      axisLabel: {
        color: chart.axisLabel,
      },
    },
    yAxis: {
      type: 'value',
      axisLine: {
        lineStyle: {
          color: chart.axisLine,
        },
      },
      splitLine: {
        lineStyle: {
          color: chart.splitLine,
        },
      },
      axisLabel: {
        color: chart.axisLabel,
      },
    },
    series: series.map((item) => ({
      name: item.name,
      type: 'line',
      smooth: true,
      symbol: 'none',
      data: item.data,
      lineStyle: {
        width: 2,
        color: NEWCOLOR[item.color],
      },
      itemStyle: {
        color: NEWCOLOR[item.color],
      },
      areaStyle: item.areaStyle
        ? {
            opacity: item.areaStyle.opacity ?? 1,
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: NEWCOLOR[item.areaStyle.startColor]!,
              },
              {
                offset: 1,
                color: NEWCOLOR[item.areaStyle.endColor]!,
              },
            ]),
          }
        : undefined,
    })),
  };

  return (
    <div className="w-full h-full min-h-0 relative">
      <ReactECharts option={option} style={{ height: '100%', width: '100%' }} />
    </div>
  );
};

export default LineChart;
